import React, { FC, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../../redux/store";
import { MenuItem, Menu} from "@mui/material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from "../../../../components/reusable-ui/material-ui-comp/accordion";
import InvoiceSummary from "../invoice-summary";
import { getCumulativeSum } from "../../../../helpers/accounting-helper";
import {
  getBillsDetails,
  getInvoiceDetails,
  getPaymentSummary,
  getPaymentUserSummary,
  getPaymentUserSummaryDetails,
} from "../../../../redux/slices";
import ZeroScreen from "../zero-screen";
import SortFilter from "../sort-filter";
import {
  ACCOUNT_SUMMARY,
  GROUP_BY_TYPES,
  GROUP_METHODS,
  PAYMENT_SUMMARY_API_SUFFIX,
  SORT_VALUE_TYPES,
} from "../../../../data/constants";
import SearchInputComponent from "../../../../components/reusable-ui/search-input";
import { allowEverything } from "../../../../utils/regex";
import { isMobileTabDevice } from "../../../../helpers/device-info-helper";
import { getGroupedData, getTotalPaymentOrder, renderAccordionHeader, renderBillDetails, renderNoResultSearch, renderPaymentByUserHeader, renderPaymentModeHeader, renderRightBillSection } from "./payment-summary-details";

interface Props {
  selectedDate: string;
  branchIds: any;
  brandName : any;
  groupBy: string;
  hideFilterMobile:any;
  showFilterResults: any;
}
interface DataItem {
  amount: number;
  count: number;
  mode: string;
  branchId: string;
}
interface DataUserItem {
  amount: number;
  branchId: string;
  businessUserId: string;
  count: number;
  userName: any;
}

const PaymentSummary: FC<Props> = ({ selectedDate, branchIds, brandName, groupBy, hideFilterMobile, showFilterResults }) => {
  const [invoiceSummary, setInvoiceSummary] = useState<boolean>(false)
  const [selectedMode, setSelectedMode] = useState<any>({});
  const [selectedOptionSet, setOptionSet] = useState<any>({});
  const [selectedUserData, setSelectedUserData] = useState<any>({});
  const [userPaymentData, setUserPaymentData] = useState<any>();
  const [billsDetailsMap, setBillsDetailsMap] = useState<any>({});
  const [searchIcon, setSearchIcon] = useState<boolean>(false);
  const [groupMethod, setGroupMethod] = useState<string>(GROUP_METHODS.Payment);
  const [paymentDetailList, setPaymentDetailList] = useState<any>({});
  const [paymentUserDetailsMap, setPaymentUserDetailsMap] = useState<any>({});
  const [searchData, setSearchData] = useState<any>({
    value: "",
    results: [],
  });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleOptionSetChange =
    (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      event.stopPropagation();
     setOptionSet(newExpanded ? panel : false)
    };

    const handleUserOptionSetChange =
    (panel: any) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      event.stopPropagation();
      setSelectedUserData(newExpanded ? panel : false);
      let dataToPost = getPayload();
      if(groupMethod===GROUP_METHODS.User){
      dataToPost.businessUserId = panel?.businessUserId}
      dispatch(getPaymentUserSummaryDetails(dataToPost));
    };
 
  const { businessBranchesDataMap, billsDetails, userPaymentDetails, paymentUserDetailsList, paymentDetails} = useSelector((state: any) => ({
    businessBranchesDataMap: state.businessReducer.businessBranchesDataMap,
    billsDetails: state.accountingReducer?.billsDetails,
    userPaymentDetails: state.accountingReducer?.userSummary,
    paymentUserDetailsList: state.accountingReducer?.paymentUserSummary,
    paymentDetails: state.accountingReducer?.paymentSummary
  }));

  const dropdownItems = [
    {
      key: "amountl2h",
      label: "Amount (Low to High)",
      getKey: (val: any) => {
        return getCumulativeSum(val, 'amount');
      },
      type: SORT_VALUE_TYPES.number,
      class: "ic-sort-increasing",
    },
    {
      key: "amounth2l",
      label: "Amount (High to Low)",
      getKey: (val: any) => {
        return -getCumulativeSum(val, 'amount');
      },
      type: SORT_VALUE_TYPES.number,
      class: "ic-sort-decreasing"
    }
  ];
  const dropdownItemsBill = [
    {
      key: "amountl2h",
      label: "Amount (Low to High)",
      getKey: (val: any) => {
        return val.amount;
      },
      type: SORT_VALUE_TYPES.number,
      class: "ic-sort-increasing",
    },
    {
      key: "amounth2l",
      label: "Amount (High to Low)",
      getKey: (val: any) => {
        return -val.amount;
      },
      type: SORT_VALUE_TYPES.number,
      class: "ic-sort-decreasing"
    }
  ];

  function getPayload() {
    return {
      branchIds:
        groupBy === GROUP_BY_TYPES.branch
          ? groupMethod === GROUP_METHODS.Payment
            ? [selectedMode?.branchId]
            : branchIds
          : branchIds,
      modes: [selectedMode?.mode],
      businessUserId: selectedUserData?.businessUserId,
      periods: [selectedDate],
      bizzBrandNames:
        groupBy === GROUP_BY_TYPES.brand
          ? [selectedMode?.bizBrandName]
          : brandName,
      groupBy: groupBy,
      isNew: true,
    };
  }

  function filterPaymentSummary(apiSuffix: any) {
    handleClose();
    let dataToPost = getPayload();
    dispatch(getBillsDetails(dataToPost));
  }
  function fetchInvoiceDetails(saleId: string) {
    dispatch(getInvoiceDetails({ saleId }));
  }
  useEffect(() => {
    if (Object.keys(selectedMode).length > 0) {
      filterPaymentSummary(PAYMENT_SUMMARY_API_SUFFIX.payment);
    }
  }, [selectedMode]);

  useEffect(() => {
    setPaymentDetailList(
      getGroupedData(
        paymentDetails,
        groupBy === GROUP_BY_TYPES.branch ? "branchId" : "bizBrandName"
      )
    );
    setPaymentUserDetailsMap(
      getGroupedData(
        paymentUserDetailsList,
        groupBy === GROUP_BY_TYPES.branch ? "branchId" : "bizBrandName"
      )
    );
    setSelectedMode("");
  }, [paymentDetails, paymentUserDetailsList]);

  useEffect(() => {
    if (billsDetails?.length > 0) setBillsDetailsMap(billsDetails);
  }, [billsDetails]);

  useEffect(() => {
    if (userPaymentDetails?.length > 0) {
      setUserPaymentData(userPaymentDetails);
    }
  }, [userPaymentDetails]);

  const onSearchInput = (searchStr: string) => {
    const results: any = [];
    if (searchStr && billsDetailsMap) {
      for (let key of Object.keys(billsDetailsMap)) {
        if (
          billsDetailsMap[key].billNumber
            .toLocaleLowerCase()
            .includes(searchStr.toLocaleLowerCase())
        ) {
          results.push(billsDetailsMap[key]);
        }
      }
    }
    setSearchData({ value: searchStr, results });
  };

  const handleInvoiceSummary = () => {
    setInvoiceSummary(!invoiceSummary);
  };

  function renderPaymentByUser(userPaymentList: any, branchId: string) {
    return Object.values(userPaymentList)?.length
      ? Object.values(userPaymentList[branchId]).map(
          (option: any, i: number) => {
            let dataToPost = {
              branchIds: branchIds,
              businessUserId: [option?.businessUserId],
              periods: [selectedDate],
              bizzBrandNames:
                groupBy === GROUP_BY_TYPES.brand
                  ? [selectedMode?.bizBrandName]
                  : brandName,
              groupBy: groupBy,
              isNew: true,
            };
            return (
              <Accordion
                key={option?.businessUserId}
                TransitionProps={{ unmountOnExit: true }}
                expanded={selectedUserData.businessUserId === option.businessUserId}
                onChange={handleUserOptionSetChange(option)}
              >
                <AccordionSummary
                  className={`accordion-header flex justify-between !hover:shadow-[0px_2px_4px_#00000014] px-16 py-12  pl-34 ${
                    selectedUserData?.userName?.firstName ===
                    option?.userName?.firstName
                      ? "active"
                      : ""
                  }
                }`}
                >
                  <section
                    key={option?.businessUserId}
                    onClick={() => {
                      setSelectedMode(option);
                      hideFilterMobile(option);
                      dispatch(getPaymentUserSummaryDetails(dataToPost));
                    }}
                    className="w-full"
                  >
                    {renderPaymentByUserHeader(option)}
                  </section>
                </AccordionSummary>

                <AccordionDetails>
                  {renderPaymentByMode(userPaymentData, i)}
                </AccordionDetails>
              </Accordion>
            );
          }
        )
      : null;
  }

  function renderPaymentByMode(options: any, index: number) {
    return (groupMethod === GROUP_METHODS.Payment ? options[index] : options)
      ?.length
      ? (groupMethod === GROUP_METHODS.Payment ? options[index] : options).map(
          (option: any, i: number) => {
            return (
              <section
                className={`p-16 ${
                  groupMethod === GROUP_METHODS.Payment ? "pl-50" : "pl-80"
                } hover:shadow-[0px_2px_4px_#00000014] relative flex items-center cursor-pointer w-full border-b-[1px]  border-b-[#ECECEC] menu-item-details ${
                  selectedMode.mode === option?.mode
                    ? "bg-[#0000001a] border-l-[3px] border-l-[#000]"
                    : ""
                }`}
                key={i}
                onClick={() => {
                  hideFilterMobile(option);
                  setSelectedMode(option);
                }}
              >
                {renderPaymentModeHeader(option)}
              </section>
            );
          }
        )
      : null;
  }

  function renderSearchResults(): React.JSX.Element {
    return searchData.results.length ? (
      <>
        {searchIcon && (
          <div className="text-14 font-normal p-16 opacity-50 border-b-[1px] border-[#ECECEC]">
            {searchData.results.length} results found
          </div>
        )}
        {renderBillDetails(
          searchData.results,
          setInvoiceSummary,
          fetchInvoiceDetails
        )}
      </>
    ) : (
      <>{renderNoResultSearch()}</>
    );
  }

  function renderPaymentModesList(optionSetsList: any) {
    return Object.keys(optionSetsList).length>0
      ? Object.keys(optionSetsList).map((options: any, index: number) => {
          return (
            <Accordion
              key={options}
              TransitionProps={{ unmountOnExit: true }}
              expanded={selectedOptionSet === options}
              onChange={handleOptionSetChange(options)}
            >
              <AccordionSummary
                className={`accordion-header py-16 pr-16 pl-12 !m-0 flex justify-between ${
                  selectedOptionSet === options ? "active" : ""
                }`}
                aria-controls="branch-data"
                id={options}
              >
                {renderAccordionHeader({
                  headerName:
                    groupBy === GROUP_BY_TYPES.branch
                      ? businessBranchesDataMap[options]?.branchName
                      : optionSetsList[options][0].bizBrandName,
                  branchesData: businessBranchesDataMap,
                  options: optionSetsList[options],
                  isSelected: selectedOptionSet === options,
                  groupMethod: groupMethod,
                  groupBy: groupBy,
                  paymentUserDetailsList: paymentUserDetailsList,
                })}
              </AccordionSummary>
              <AccordionDetails className="!m-0">
                {groupMethod == GROUP_METHODS.User
                  ? renderPaymentByUser(optionSetsList, options)
                  : renderPaymentByMode(Object.values(optionSetsList), index)}
              </AccordionDetails>
            </Accordion>
          );
        })
      : null;
  }

  function renderModeSelector(): React.JSX.Element{
    let dataToPost = getPayload();
    return (
      <div className="flex justify-between">
        <div className={`w-[117px] !p-[8px] smd:p-1 text-[13px] border rounded-[6px] flex gap-1 justify-center items-center overflow-hidden mr-5 hover:border-black  ${open? " border-black":"border-[#3a5e8b1a]"}`}>
          <span>Group:</span>
          <Menu
            id="long-menu"
            MenuListProps={{
              "aria-labelledby": "long-button",
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
          >
            <MenuItem onClick={handleClose}>
              <p
                onClick={() => {
                  setGroupMethod(GROUP_METHODS.Payment);
                  dispatch(getPaymentSummary(dataToPost));
                }}
                className="text-[14px]"
              >
                Payment
              </p>
            </MenuItem>
            <MenuItem onClick={handleClose}>
              <p
                onClick={() => {
                  setGroupMethod(GROUP_METHODS.User);
                  dispatch(getPaymentUserSummary(dataToPost));
                }}
                className="text-[14px]"
              >
                User
              </p>
            </MenuItem>
          </Menu>
          <span
            className="ml-2 text-[#397EF5] cursor-pointer"
            onClick={handleClick}
          >
            {groupMethod}
          </span>
        </div>
        <SortFilter
          dataToSort={
            groupMethod === GROUP_METHODS.Payment
              ? Object.values(paymentDetailList)
              : Object.values(paymentUserDetailsMap)
          }
          handleSort={
            groupMethod === GROUP_METHODS.Payment
              ? setPaymentDetailList
              : setPaymentUserDetailsMap
          }
          sortOptionsMap={dropdownItems}
        />
      </div>
    );
  }

  function renderAmountHeader(): React.JSX.Element{
    return  <div className="w-[40%] smd:mt-0 smd:ml-0 smd:w-[100%] border-r-[1px] mt-12 ml-16 smd:m-0 bg-white">
    {getTotalPaymentOrder(groupMethod === GROUP_METHODS.Payment
                        ? paymentDetails
                        : paymentUserDetailsList)}
    <div className="text-[#0000007A] bg-white items-center text-xs py-12 px-16 text-14 flex justify-between border-b-[1px]">
      <div className="text-[14px] font-semibold">
        {groupBy===GROUP_BY_TYPES.branch?"Branches:":"Brands:"}
        {Object.keys(
          groupMethod === GROUP_METHODS.Payment
            ? paymentDetailList
            : paymentUserDetailsMap
        ).length}
      </div>
      {renderModeSelector()}
    </div>
    <div className="h-[calc(100vh-312px)] overflow-y-auto pb-[8px] bg-white">
      {renderPaymentModesList(
        groupMethod === GROUP_METHODS.Payment
          ? paymentDetailList
          : paymentUserDetailsMap
      )}
    </div>
  </div>
  }
  
  function renderInvoiceHeader(): React.JSX.Element{
    return (
      <>
        {!invoiceSummary && Object.keys(selectedMode).length > 0 && (
          <div className="sale-summary-container border border-[#DDDDDD] rounded-[12px] m-24 smd:m-20">
            <div className="relative pt-[74px] h-[100%]">
              <div
                className={`sale-summary-header flex justify-between items-center p-16 border-b border-[#DDDDDD]`}
              >
                {!searchIcon ? (
                  <>
                    {renderRightBillSection(
                      setSelectedMode,
                      selectedMode,
                      hideFilterMobile,
                      billsDetails,
                      setSearchIcon,
                      setBillsDetailsMap,
                      dropdownItemsBill
                    )}
                  </>
                ) : (
                  <>
                    <SearchInputComponent
                      placeholder={"Search..."}
                      customClass={"text-15 py-12 outline-[#9FCAFD]"}
                      onSerachInput={onSearchInput}
                      searchMinLength={3}
                      regexCheck={allowEverything}
                      elementWidthClass={"w-[90%]"}
                      autoFocus={true}
                    />
                    <span
                      className="rista-sprite ic-cross-black"
                      onClick={() => setSearchIcon(false)}
                    ></span>
                  </>
                )}
              </div>
              <div className="sale-summary-body">
                {searchData.value.length >= 3
                  ? renderSearchResults()
                  : renderBillDetails(
                      billsDetailsMap ? Object.values(billsDetailsMap) : [],
                      setInvoiceSummary,
                      fetchInvoiceDetails
                    )}
              </div>
            </div>
          </div>
        )}
        {invoiceSummary && (
          <InvoiceSummary closeInvoiceDetails={handleInvoiceSummary} />
        )}
      </>
    );
  }

   function renderPaymentSummary(): React.JSX.Element {
     //condition to check before rendering invoice header
     let isRenderInvoiceHeader =
       (!isMobileTabDevice() ||
         (isMobileTabDevice() && Object.keys(selectedMode).length > 0)) &&
       billsDetails.length > 0;
     return (
       <div className={`flex smd:flex-col `}>
         {(!isMobileTabDevice() ||
           (isMobileTabDevice() && billsDetails.length === 0)) &&
           renderAmountHeader()}
           <div className="w-[60%] bg-white  smd:w-[100%] smd:mt-0 mt-12 mr-16 smd:p-4">
           {isRenderInvoiceHeader ? renderInvoiceHeader(): !isMobileTabDevice() && <ZeroScreen showSummaryDetails={true} summaryType={ACCOUNT_SUMMARY.payment.id}/>}
           </div>
       </div>
     );
   }
  return (
    <>
      {(groupMethod=== GROUP_METHODS.Payment && Object.keys(paymentDetailList).length > 0) ||
      (groupMethod=== GROUP_METHODS.User && Object.keys(paymentUserDetailsMap).length > 0) ? (
                renderPaymentSummary()
      ) : (
        <ZeroScreen showFilterResults={showFilterResults} />
      )}
    </>
  );
};
export default PaymentSummary;
